import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Shield, Save } from "lucide-react";
import { VoiceAgent } from "@/services/voiceAgentService";

interface VoiceAgentAdminTabProps {
    agent: VoiceAgent;
    updateAgent: (updates: Partial<VoiceAgent>) => void;
}

// Define the update data type for better type safety
type VoiceAgentUpdateData = Partial<{
    is_template: boolean;
    hidden_template_instructions: string | null;
    industry_id: string | null;
    persona_id: string | null;
    description: string | null;
}>;

interface Industry {
    id: string;
    name: string;
    emoji: string | null;
}

interface Persona {
    id: string;
    avatar_name: string | null;
    type: string | null;
}

// Form validation schema for admin fields
const adminFormSchema = z.object({
    is_template: z.boolean().default(false),
    hidden_template_instructions: z.string().optional(),
    industry_id: z.string().optional(),
    persona_id: z.string().optional(),
    description: z.string().optional(),
});

type AdminFormValues = z.infer<typeof adminFormSchema>;

const VoiceAgentAdminTab: React.FC<VoiceAgentAdminTabProps> = ({ agent, updateAgent }) => {
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [personas, setPersonas] = useState<Persona[]>([]);

    const form = useForm<AdminFormValues>({
        resolver: zodResolver(adminFormSchema),
        defaultValues: {
            is_template: agent.is_template || false,
            hidden_template_instructions: agent.hidden_template_instructions || "",
            industry_id: agent.industry_id || "none",
            persona_id: agent.persona_id || "none",
            description: agent.description || "",
        },
    });

    // Fetch industries and personas
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch industries
                const { data: industriesData, error: industriesError } = await supabase
                    .from("industries")
                    .select("id, name, emoji")
                    .order("name");

                if (industriesError) {
                    console.error("Error fetching industries:", industriesError);
                    toast({
                        variant: "destructive",
                        title: "Failed to load industries",
                        description: "Could not load industry options. Please try again.",
                    });
                } else {
                    setIndustries(industriesData || []);
                }

                // Fetch personas
                const { data: personasData, error: personasError } = await supabase
                    .from("personas")
                    .select("id, avatar_name, type")
                    .order("avatar_name");

                if (personasError) {
                    console.error("Error fetching personas:", personasError);
                    toast({
                        variant: "destructive",
                        title: "Failed to load personas",
                        description: "Could not load persona options. Please try again.",
                    });
                } else {
                    setPersonas(personasData || []);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, []);

    // Update form when agent changes
    useEffect(() => {
        form.reset({
            is_template: agent.is_template || false,
            hidden_template_instructions: agent.hidden_template_instructions || "",
            industry_id: agent.industry_id || "none",
            persona_id: agent.persona_id || "none",
            description: agent.description || "",
        });
    }, [agent, form]);

    const onSubmit = async (values: AdminFormValues) => {
        setIsLoading(true);
        try {
            // Prepare update data
            const updateData: VoiceAgentUpdateData = {
                is_template: values.is_template,
                hidden_template_instructions: values.hidden_template_instructions || null,
                industry_id: values.industry_id === "none" ? null : values.industry_id,
                persona_id: values.persona_id === "none" ? null : values.persona_id,
                description: values.description || null,
            };

            // Update the voice agent in the database
            const { error } = await supabase
                .from("voice_agents")
                .update(updateData)
                .eq("id", agent.id);

            if (error) {
                throw error;
            }

            // Update the local agent state
            updateAgent(updateData);

            toast({
                title: "Voice agent updated",
                description: "Admin settings have been updated successfully.",
            });
        } catch (error: any) {
            console.error("Error updating voice agent:", error);
            toast({
                variant: "destructive",
                title: "Error",
                description: error.message || "Failed to update voice agent admin settings",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 mb-6">
                <Shield className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">Voice Agent Admin Settings</h2>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Template Management</CardTitle>
                    <CardDescription>
                        Configure template settings and administrative options for this voice agent
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            {/* Template Toggle */}
                            <FormField
                                control={form.control}
                                name="is_template"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">
                                                Mark as Template
                                            </FormLabel>
                                            <FormDescription>
                                                When enabled, this voice agent will appear in the template selection for all users
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {/* Description Field */}
                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Voice agent description visible to users"
                                                className="min-h-[100px]"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            This description will be visible to users when they view or select this voice agent
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Hidden Template Instructions */}
                            <FormField
                                control={form.control}
                                name="hidden_template_instructions"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Hidden Template Instructions</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Internal instructions for this template (not visible to users)"
                                                className="min-h-[100px]"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            These instructions are only visible to super admins and are used for internal documentation
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Industry Selection */}
                            <FormField
                                control={form.control}
                                name="industry_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Industry</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select an industry" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="none">No industry</SelectItem>
                                                {industries.map((industry) => (
                                                    <SelectItem key={industry.id} value={industry.id}>
                                                        {industry.emoji} {industry.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>
                                            Associate this voice agent with a specific industry for better categorization
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Persona Selection */}
                            <FormField
                                control={form.control}
                                name="persona_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Persona</FormLabel>
                                        <Select onValueChange={field.onChange} value={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a persona" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="none">No persona</SelectItem>
                                                {personas.map((persona) => (
                                                    <SelectItem key={persona.id} value={persona.id}>
                                                        {persona.avatar_name} {persona.type && `(${persona.type})`}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>
                                            Associate this voice agent with a persona for better user experience
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Submit Button */}
                            <div className="flex justify-end">
                                <Button type="submit" disabled={isLoading}>
                                    <Save className="h-4 w-4 mr-2" />
                                    {isLoading ? "Saving..." : "Save Changes"}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};

export default VoiceAgentAdminTab; 